import _ from 'underscore';

import { Module } from '../Module';

interface IRadioButtonScope extends ng.IScope {
  classForState: () => string;
  selected: () => boolean;
  disabled: boolean;
  select: () => void;
  value: () => any;
}

/* Custom radio button control. Two ways to use:
*
*  1. Two-way binding to a scope property, like in a form. Multiple radio buttons can be bound
*     to the same ng-model. The tb-value attribute is a JavaScript expression that evaluates
*     to the target value; when the radio button is clicked, the model is assigned the target
*     value.
*     Includes all the fancy Angular form features: http://docs.angularjs.org/guide/forms
*
*       tb:radio-button(ng-model="model.bool")
*
*     The ng-model can also be an array, in which case the radio buttons can be tri-state:
*       If all of the items match tb-value, then the button will appear selected;
*       If some, but not all, of the items match tb-value, then the button will appear mixed;
*       If none of the items match tb-value, then the button will appear empty.
*
*  2. Reflect value of expressions. You'll have to define an ng-click if you want behavior on click.
*     For this usage, omit the ng-model attribute. This version works only for dual-state radio buttons.
*
*       tb:radio-button(selected="amIAwesome()")
*
*
*
*   For a radio button with a clickable label:
*
*     tb:radio-button-group
*       tb:radio-button(
*         ng-model="model",
*         tb-value="target value expression")
*       tb:radio-button-label Content
*
*   To disable, add disabled=true to the tbRadioButton. This will also disable any
*   tbRadioButtonLabel in the tbRadioButtonGroup:
*
*     tb:radio-button(disabled='myExpressionEvaluatingToTrue()')
*
*/
Module.instance().directive('tbRadioButton', <any[]>[
  () => {

    const SELECTED_CLASS: string = 'tb-selected';
    const MIXED_CLASS: string = 'tb-mixed';
    const EMPTY_CLASS: string = '';

    return {
      restrict: 'E',
      replace: true,
      require: ['?ngModel', '^?tbRadioButtonGroup'],
      templateUrl: 'radioButton.html',
      scope: {
        selected: '&',
        value: '&tbValue',
        disabled: '='
      },
      link: (scope: IRadioButtonScope, elm: JQuery, attrs: ng.IAttributes, ctrls: any[]) => {
        const ngModelCtrl = <ng.INgModelController> ctrls[0];
        const groupCtrl = <RadioButtonGroupCtrl> ctrls[1];

        // If the radio button is instantiated to reflect the value of an expression (#2, above),
        // there is no ngModel and we use the selected() function that was passed in.
        if(!ngModelCtrl) {
          scope.classForState = () => {
            return scope.selected() ? SELECTED_CLASS : EMPTY_CLASS;
          };
          return;
        }
        scope.classForState = () => {
          if (!_.isArray(ngModelCtrl.$viewValue)) {
            return ngModelCtrl.$viewValue === scope.value() ? SELECTED_CLASS : EMPTY_CLASS;
          }

          // for array ngModels, the radio button could be mixed
          const array: any[] = ngModelCtrl.$viewValue;
          let count = 0;
          for (let i = 0; i < array.length; i++) {
            if (array[i] === scope.value()) count++;
          }

          if (count === array.length) {
            return SELECTED_CLASS;
          } else if (count === 0) {
            return EMPTY_CLASS;
          } else {
            return MIXED_CLASS;
          }
        };

        scope.select = () => {
          if (scope.disabled) return;
          if (_.isArray(ngModelCtrl.$viewValue)) {
            ngModelCtrl.$setViewValue(_.map(ngModelCtrl.$viewValue, () => {
              return scope.value();
            }));
          } else {
            ngModelCtrl.$setViewValue(scope.value());
          }
          ngModelCtrl.$render();
        };

        if (groupCtrl) {
          // Links the functions for the radio button to the radio button group.
          groupCtrl.select = scope.select;
          groupCtrl.disabled = () => scope.disabled;
        }
      }
    };
  }
]);

class RadioButtonGroupCtrl {
  public select: () => void;
  public disabled: () => boolean;
}

Module.instance().directive('tbRadioButtonGroup', <any[]>[
  () => {
    return {
      restrict: 'E',
      transclude: true,
      replace: true,
      template: '<div class="tb-radio-button-group" ng-transclude></div>',
      controller: RadioButtonGroupCtrl
    };
  }
]);

interface IRadioButtonLabelScope extends ng.IScope {
  ctrl: RadioButtonGroupCtrl;
}

Module.instance().directive('tbRadioButtonLabel', <any[]>[
  () => {
    return {
      restrict: 'E',
      transclude: true,
      replace: true,
      require: '^tbRadioButtonGroup',
      templateUrl: 'radioButtonLabel.html',
      link: ($scope: IRadioButtonLabelScope,
          elm: JQuery,
          attrs: ng.IAttributes,
          ctrl: RadioButtonGroupCtrl) => {
        $scope.ctrl = ctrl;
      }
    };
  }
]);
